import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// BPO RH — Controle de Folgas (abono assiduidade da CCT — Setho e Mercatto).
// Quem não falta no período aquisitivo (ou tem até 2 faltas justificadas, fora as
// do art. 473) ganha 3 folgas para usar nos 12 meses seguintes. A fonte é o ponto:
// cada mês fechado é guardado no Saber (o RHiD demora minutos por mês) e a
// apuração acontece quando o colaborador completa o ano. Atestado não conta.
// Backend: core/views_folgas_assiduidade.py
const API = process.env.REACT_APP_API_BASE;
const URL_F = `${API}/api/bporh/folgas-assiduidade/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const erroMsg = (e, padrao) => e.response?.data?.mensagem || e.response?.data?.erro || padrao;
const mesRot = (m) => `${m.slice(5)}/${m.slice(0, 4)}`;

const SITUACAO = {
  tem_direito: { rotulo: 'Tem direito', bg: '#e6f7ef', cor: '#116b47', icone: 'fa-circle-check' },
  perdeu: { rotulo: 'Perdeu', bg: '#fdeaec', cor: '#a11d2b', icone: 'fa-circle-xmark' },
  ja_perdeu: { rotulo: 'Já perdeu (período aberto)', bg: '#fdeaec', cor: '#a11d2b', icone: 'fa-circle-xmark' },
  em_apuracao: { rotulo: 'Em apuração', bg: '#e3f6fb', cor: '#0e6e86', icone: 'fa-hourglass-half' },
  sem_dados: { rotulo: 'Confirmar (sem ponto)', bg: '#fff5da', cor: '#8a6d00', icone: 'fa-circle-question' },
};

const Selo = ({ sit }) => {
  const s = SITUACAO[sit] || SITUACAO.em_apuracao;
  return (
    <span className="badge" style={{ background: s.bg, color: s.cor, border: `1px solid ${s.cor}33` }}>
      <i className={`fas ${s.icone} me-1`}></i>{s.rotulo}
    </span>
  );
};

export default function BpoRhControleFolgas() {
  const [params, setParams] = useSearchParams();
  const [clientes, setClientes] = useState(null);
  const clienteId = params.get('cliente') || '';
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [carga, setCarga] = useState(null);          // {atual, total, mes}
  const [aberto, setAberto] = useState(null);        // convenia_id do detalhe
  const [ajuste, setAjuste] = useState(null);        // {convenia_id, iso, data, classe, motivo}
  const [uso, setUso] = useState(null);              // {convenia_id, periodo_inicio, data, obs}
  const [decisao, setDecisao] = useState(null);      // {convenia_id, periodo_inicio, decisao, motivo}
  const [busca, setBusca] = useState('');
  // envio da planilha ao cliente: {para, copia, operador_id, previa, carregando}
  const [envio, setEnvio] = useState(null);       // dados de envio do backend (para, operadores, histórico)
  const [email, setEmail] = useState(null);
  const [enviando, setEnviando] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(URL_F, { params: { cliente_id: clienteId || undefined }, headers: headers() })
      .then((r) => {
        setClientes(r.data.clientes || []);
        setDados(r.data.dados);
        setEnvio(r.data.envio || null);
        if (!clienteId && (r.data.clientes || []).length === 1) {
          setParams({ cliente: String(r.data.clientes[0].id) });
        }
      })
      .catch((e) => setMensagem(`❌ ${erroMsg(e, 'Não foi possível carregar')}`))
      .finally(() => setCarregando(false));
  }, [clienteId, setParams]);
  useEffect(() => { carregar(); }, [carregar]);

  // guarda o ponto dos meses que faltam, um por vez (o RHiD leva minutos por mês)
  const carregarMeses = async () => {
    const meses = dados?.meses_faltando || [];
    for (let i = 0; i < meses.length; i += 1) {
      setCarga({ atual: i + 1, total: meses.length, mes: meses[i] });
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(URL_F, { acao: 'carregar_mes', cliente_id: Number(clienteId), mes: meses[i] },
          { headers: headers(), timeout: 600000 });
      } catch (e) {
        setMensagem(`❌ ${mesRot(meses[i])}: ${erroMsg(e, 'não foi possível ler o ponto')}`);
        break;
      }
    }
    setCarga(null);
    carregar();
  };

  const postar = (corpo, depois) => axios.post(URL_F, { cliente_id: Number(clienteId), ...corpo }, { headers: headers() })
    .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); depois?.(); carregar(); })
    .catch((e) => setMensagem(`❌ ${erroMsg(e, 'Não foi possível salvar')}`));

  const baixarPlanilha = () => {
    setEnviando('baixar');
    axios.get(URL_F, { params: { cliente_id: clienteId, exportar: 1, _: Date.now() }, headers: headers(), responseType: 'blob' })
      .then((r) => {
        const m = (r.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/i);
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = m ? m[1] : 'Controle_de_Folgas.xlsx';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
      })
      .catch(() => setMensagem('❌ Não foi possível gerar a planilha'))
      .finally(() => setEnviando(''));
  };
  const verPrevia = (e) => {
    setEmail((x) => ({ ...(x || e), carregando: true }));
    axios.post(URL_F, { acao: 'email', enviar: false, cliente_id: Number(clienteId),
      destinatarios: e.para, copia: e.copia, operador_id: e.operador_id || undefined }, { headers: headers(), timeout: 120000 })
      .then((r) => setEmail((x) => ({ ...(x || e), previa: r.data.previa, carregando: false })))
      .catch((er) => { setEmail((x) => (x ? { ...x, carregando: false } : x)); setMensagem(`❌ ${erroMsg(er, 'Não foi possível montar o e-mail')}`); });
  };
  const abrirEmail = () => {
    const novo = { para: envio?.para || '', copia: envio?.copia || '', operador_id: String(envio?.operador_sugerido_id || ''),
                   previa: null, carregando: false };
    setEmail(novo);
    verPrevia(novo);
  };
  const mudarEmail = (campo, valor) => setEmail((x) => ({ ...x, [campo]: valor, previa: null }));
  const salvarEmails = () => {
    setEnviando('salvar');
    axios.post(URL_F, { acao: 'salvar_emails', cliente_id: Number(clienteId), destinatarios: email.para, copia: email.copia },
      { headers: headers() })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setEnvio(r.data.envio); })
      .catch((er) => setMensagem(`❌ ${erroMsg(er, 'Não foi possível salvar os e-mails')}`))
      .finally(() => setEnviando(''));
  };
  const enviarEmail = () => {
    setEnviando('email');
    axios.post(URL_F, { acao: 'email', enviar: true, cliente_id: Number(clienteId), destinatarios: email.para,
      copia: email.copia, operador_id: email.operador_id || undefined, assunto: email.previa?.assunto,
      corpo_html: email.previa?.corpo_html }, { headers: headers(), timeout: 180000 })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setEnvio(r.data.envio); setEmail(null); })
      .catch((er) => setMensagem(`❌ ${erroMsg(er, 'Não foi possível enviar')}`))
      .finally(() => setEnviando(''));
  };

  const lista = useMemo(() => {
    const b = busca.trim().toLowerCase();
    return (dados?.colaboradores || []).filter(c => !b || c.nome.toLowerCase().includes(b));
  }, [dados, busca]);
  const detalhe = (dados?.colaboradores || []).find(c => c.convenia_id === aberto);

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-umbrella-beach header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">BPO RH — Controle de Folgas</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 230 }} value={clienteId}
            onChange={(e) => setParams(e.target.value ? { cliente: e.target.value } : {})} aria-label="Empresa">
            <option value="">{clientes ? 'Escolha a empresa…' : 'Carregando…'}</option>
            {(clientes || []).map((c) => <option key={c.id} value={c.id}>{c.cliente}</option>)}
          </select>
          <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
            <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
          </button>
          {dados && (<>
            <button type="button" className="gr-btn" onClick={baixarPlanilha} disabled={!!enviando}
              title="Baixar a planilha do controle de folgas (para conferir)">
              <i className={`fas ${enviando === 'baixar' ? 'fa-spinner fa-spin' : 'fa-file-excel'}`} aria-hidden="true"></i>Planilha
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={abrirEmail} disabled={!!enviando}
              title="Mandar a planilha e o resumo ao cliente por e-mail (com prévia)">
              <i className="fas fa-paper-plane me-1" aria-hidden="true"></i>Enviar ao cliente
            </button>
          </>)}
        </div>
      </div>

      <div className="small text-muted mb-3">
        Abono assiduidade da CCT: quem não tiver falta no período aquisitivo, ou tiver até 2 faltas justificadas
        (fora as do art. 473 da CLT), ganha <b>3 folgas</b> para usar nos 12 meses seguintes. Fonte: o ponto.
        Atestado médico não conta como falta.
      </div>

      {!clienteId && <div className="text-muted small py-4 text-center">Escolha a empresa para começar.</div>}
      {dados && (envio?.historico || []).length > 0 && (
        <div className="small text-muted mb-2">
          <i className="fas fa-envelope-circle-check me-1 text-success"></i>
          Enviado ao cliente: {envio.historico.map((h) => `${h.em} para ${h.para}${h.por ? ` (${h.por})` : ''}`).join(' · ')}
        </div>
      )}

      {dados && (<>
        {/* histórico do ponto guardado no Saber */}
        <div className="border rounded px-3 py-2 mb-3 small d-flex flex-wrap align-items-center gap-3" style={{ background: '#f8f9fa' }}>
          <span><i className="fas fa-database me-1 text-muted"></i>
            Ponto guardado: <b>{dados.meses_carregados.length}</b> mês(es) desde {dados.inicio_historico}
          </span>
          {dados.meses_faltando.length > 0 ? (
            <>
              <span className="text-warning-emphasis">
                <i className="fas fa-triangle-exclamation me-1"></i>
                Faltam {dados.meses_faltando.length}: {dados.meses_faltando.map(mesRot).join(', ')}
              </span>
              <button type="button" className="btn btn-sm btn-primary" onClick={carregarMeses} disabled={!!carga}>
                {carga
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Lendo {mesRot(carga.mes)} ({carga.atual}/{carga.total})…</>
                  : <><i className="fas fa-download me-2"></i>Guardar o ponto que falta</>}
              </button>
              {carga && <span className="text-muted">o RHiD leva de 20 s a 3 min por mês — pode deixar a tela aberta</span>}
            </>
          ) : <span className="text-success"><i className="fas fa-circle-check me-1"></i>em dia até o último mês fechado</span>}
        </div>

        <div className="d-flex gap-2 flex-wrap mb-3">
          <span className="badge fs-6" style={{ background: '#e6f7ef', color: '#116b47', border: '1px solid #1fa971' }}>
            {dados.resumo.com_saldo} com folgas a usar
          </span>
          <span className="badge fs-6" style={{ background: '#e3f6fb', color: '#0e6e86', border: '1px solid #b3e3ef' }}>
            {dados.resumo.completam_30d} completam 1 ano em 30 dias
          </span>
          {dados.resumo.vencem_60d > 0 && (
            <span className="badge fs-6" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}>
              {dados.resumo.vencem_60d} com folga vencendo em 60 dias
            </span>
          )}
          {dados.resumo.sem_dados > 0 && (
            <span className="badge fs-6" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}>
              {dados.resumo.sem_dados} para confirmar
            </span>
          )}
          <input className="form-control form-control-sm ms-auto" style={{ width: 240 }} placeholder="Buscar colaborador"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Colaborador</th><th>Admissão</th><th>Período atual</th><th>Faltas no período</th>
                <th>Último período fechado</th><th className="text-center">Folgas</th><th>Usar até</th><th></th>
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => {
                const atual = c.periodos.find(p => p.aberto);
                const fechados = c.periodos.filter(p => !p.aberto);
                const ult = fechados[fechados.length - 1];
                return (
                  <tr key={c.convenia_id}>
                    <td><div className="fw-semibold">{c.nome}</div><div className="small text-muted">{c.cargo || ''}</div></td>
                    <td>{c.admissao}</td>
                    <td className="small">
                      {atual ? <>{atual.inicio} – {atual.fim}
                        <div className={atual.dias_para_fechar <= 30 ? 'fw-semibold' : 'text-muted'} style={atual.dias_para_fechar <= 30 ? { color: '#0e6e86' } : {}}>
                          completa em {atual.dias_para_fechar} dia(s)
                        </div></> : '—'}
                    </td>
                    <td>
                      {atual && (<>
                        <Selo sit={atual.situacao} />
                        <div className="small text-muted mt-1">{atual.injustificadas} injust. · {atual.justificadas} justif.</div>
                      </>)}
                    </td>
                    <td>{ult ? <><Selo sit={ult.situacao} /><div className="small text-muted mt-1">{ult.inicio} – {ult.fim}</div></> : <span className="small text-muted">ainda não fechou</span>}</td>
                    <td className="text-center">
                      {ult && ult.situacao === 'tem_direito'
                        ? <span className={ult.vencida ? 'text-danger' : 'fw-bold'}>{ult.saldo} de {ult.folgas}{ult.vencida ? ' (vencida)' : ''}</span>
                        : '—'}
                    </td>
                    <td className="small">{ult && ult.situacao === 'tem_direito' ? ult.usar_ate : '—'}</td>
                    <td className="text-end">
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setAberto(c.convenia_id)}>
                        <i className="fas fa-magnifying-glass me-1"></i>Detalhes
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!lista.length && <tr><td colSpan={8} className="text-center text-muted py-4">Nenhum colaborador.</td></tr>}
            </tbody>
          </table>
        </div>
      </>)}

      {/* ─── detalhe do colaborador ─── */}
      {detalhe && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-umbrella-beach me-2"></i>{detalhe.nome}
                  </h5>
                  <small style={{ color: '#6c757d' }}>admitido em {detalhe.admissao} · {detalhe.departamento || ''}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setAberto(null)}></button>
              </div>
              <div className="modal-body">
                {[...detalhe.periodos].reverse().map((p) => (
                  <div key={p.inicio_iso} className="border rounded p-3 mb-3">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <b>Período {p.inicio} – {p.fim}</b>
                      <Selo sit={p.situacao} />
                      <span className="small text-muted">{p.injustificadas} injustificada(s) · {p.justificadas} justificada(s) (limite 2)</span>
                      {p.situacao === 'tem_direito' && (
                        <span className="small ms-auto">
                          Folgas: <b>{p.saldo} de {p.folgas}</b> · usar até <b>{p.usar_ate}</b>
                          {p.vencida && <span className="text-danger ms-1">(vencida)</span>}
                        </span>
                      )}
                    </div>
                    {(p.meses_sem_ponto.length > 0 || p.antes_do_ponto) && (
                      <div className="small text-warning-emphasis mb-2">
                        <i className="fas fa-triangle-exclamation me-1"></i>
                        {p.antes_do_ponto ? `O período começa antes de ${dados.inicio_historico}, quando o ponto ainda não estava no provedor. ` : ''}
                        {p.meses_sem_ponto.length > 0 ? `Sem ponto guardado: ${p.meses_sem_ponto.map(mesRot).join(', ')}. ` : ''}
                        {!p.aberto && 'Confirme o direito à mão.'}
                      </div>
                    )}
                    {p.decisao && (
                      <div className="small mb-2" style={{ color: '#0e6e86' }}>
                        <i className="fas fa-user-check me-1"></i>Decidido à mão por {p.decisao.autor}: {p.decisao.motivo}
                        <button type="button" className="btn btn-link btn-sm p-0 ms-2"
                          onClick={() => postar({ acao: 'decidir', convenia_id: detalhe.convenia_id, periodo_inicio: p.inicio_iso, decisao: '' })}>desfazer</button>
                      </div>
                    )}

                    {p.ocorrencias.length > 0 ? (
                      <table className="table table-sm small mb-2">
                        <thead className="table-light"><tr><th>Dia</th><th>No ponto</th><th>Conta como</th><th></th></tr></thead>
                        <tbody>
                          {p.ocorrencias.map((o) => (
                            <tr key={o.iso}>
                              <td>{o.data}</td>
                              <td>{o.status === 'sem_batida' ? 'Não bateu' : (o.texto || o.status)}</td>
                              <td>
                                <b style={{ color: o.classe === 'injustificada' ? '#a11d2b' : (o.classe === 'justificada' ? '#8a6d00' : '#116b47') }}>
                                  {dados.classes[o.classe] || 'Não conta'}
                                </b>
                                {o.ajuste && <div className="text-muted">reclassificado por {o.ajuste.autor}: {o.ajuste.motivo}</div>}
                              </td>
                              <td className="text-end">
                                <button type="button" className="btn btn-sm btn-light border"
                                  onClick={() => setAjuste({ convenia_id: detalhe.convenia_id, iso: o.iso, data: o.data,
                                    classe: o.ajuste?.classe || '', motivo: o.ajuste?.motivo || '' })}>
                                  <i className="fas fa-pen me-1"></i>Reclassificar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : <div className="small text-muted mb-2">Nenhuma falta no ponto guardado.</div>}

                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {p.situacao === 'tem_direito' && p.saldo > 0 && (
                        <button type="button" className="btn btn-sm btn-outline-success"
                          onClick={() => setUso({ convenia_id: detalhe.convenia_id, periodo_inicio: p.inicio_iso, data: '', obs: '' })}>
                          <i className="fas fa-calendar-plus me-1"></i>Registrar folga usada
                        </button>
                      )}
                      {!p.aberto && !p.decisao && (
                        <button type="button" className="btn btn-sm btn-outline-secondary"
                          onClick={() => setDecisao({ convenia_id: detalhe.convenia_id, periodo_inicio: p.inicio_iso,
                            decisao: p.situacao === 'perdeu' ? 'tem_direito' : 'tem_direito', motivo: '' })}>
                          <i className="fas fa-gavel me-1"></i>Decidir à mão
                        </button>
                      )}
                      {p.usos.map((u) => (
                        <span key={u.id} className="badge bg-light text-dark border">
                          <i className="fas fa-umbrella-beach me-1"></i>{u.data}{u.obs ? ` · ${u.obs}` : ''}
                          <button type="button" className="btn btn-link btn-sm p-0 ms-1 text-danger"
                            title="Remover" onClick={() => postar({ acao: 'desfazer_uso', id: u.id })}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setAberto(null)}><i className="fas fa-times me-2"></i>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── reclassificar um dia ─── */}
      {ajuste && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10001 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>Reclassificar {ajuste.data}</h5>
                <button type="button" className="btn-close" onClick={() => setAjuste(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label small fw-semibold">Conta como</label>
                <select className="form-select form-select-sm mb-2" value={ajuste.classe}
                  onChange={(e) => setAjuste(a => ({ ...a, classe: e.target.value }))}>
                  <option value="">O que o ponto diz (sem ajuste)</option>
                  {Object.entries(dados?.classes || {}).map(([k, r]) => <option key={k} value={k}>{r}</option>)}
                </select>
                <label className="form-label small fw-semibold">Motivo</label>
                <input className="form-control form-control-sm" value={ajuste.motivo} placeholder="Ex.: falecimento do pai (art. 473, I)"
                  onChange={(e) => setAjuste(a => ({ ...a, motivo: e.target.value }))} />
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setAjuste(null)}>Cancelar</button>
                <button className="btn btn-primary" disabled={ajuste.classe && !ajuste.motivo.trim()}
                  onClick={() => postar({ acao: 'ajustar', convenia_id: ajuste.convenia_id, data: ajuste.iso,
                    classe: ajuste.classe, motivo: ajuste.motivo }, () => setAjuste(null))}>Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── folga usada ─── */}
      {uso && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10001 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>Registrar folga usada</h5>
                <button type="button" className="btn-close" onClick={() => setUso(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label small fw-semibold">Dia da folga</label>
                <input type="date" className="form-control form-control-sm mb-2" value={uso.data}
                  onChange={(e) => setUso(u => ({ ...u, data: e.target.value }))} />
                <label className="form-label small fw-semibold">Observação (opcional)</label>
                <input className="form-control form-control-sm" value={uso.obs}
                  onChange={(e) => setUso(u => ({ ...u, obs: e.target.value }))} />
                <div className="form-text">Lance a folga também no RHiD: o Saber só controla o saldo.</div>
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setUso(null)}>Cancelar</button>
                <button className="btn btn-success" disabled={!uso.data}
                  onClick={() => postar({ acao: 'usar', ...uso }, () => setUso(null))}>Registrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── decisão manual (período sem ponto guardado) ─── */}
      {decisao && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10001 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>Decidir o período à mão</h5>
                <button type="button" className="btn-close" onClick={() => setDecisao(null)}></button>
              </div>
              <div className="modal-body">
                <select className="form-select form-select-sm mb-2" value={decisao.decisao}
                  onChange={(e) => setDecisao(d => ({ ...d, decisao: e.target.value }))}>
                  <option value="tem_direito">Tem direito às 3 folgas</option>
                  <option value="perdeu">Perdeu o direito</option>
                </select>
                <input className="form-control form-control-sm" value={decisao.motivo}
                  placeholder="Motivo (ex.: conferido no espelho de ponto antigo)"
                  onChange={(e) => setDecisao(d => ({ ...d, motivo: e.target.value }))} />
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setDecisao(null)}>Cancelar</button>
                <button className="btn btn-primary" disabled={!decisao.motivo.trim()}
                  onClick={() => postar({ acao: 'decidir', ...decisao }, () => setDecisao(null))}>Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {email && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-paper-plane me-2"></i>Enviar controle de folgas ao cliente
                  </h5>
                  <small style={{ color: '#6c757d' }}>{(clientes || []).find((c) => String(c.id) === String(clienteId))?.cliente} · planilha anexada</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setEmail(null)} disabled={enviando === 'email'}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Para</label>
                    <input type="text" className="form-control form-control-sm" placeholder="rh@cliente.com.br"
                      value={email.para} onChange={(e) => mudarEmail('para', e.target.value)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Cópia (opcional)</label>
                    <input type="text" className="form-control form-control-sm" value={email.copia}
                      onChange={(e) => mudarEmail('copia', e.target.value)} />
                  </div>
                </div>
                <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
                  <div>
                    <label className="form-label small fw-semibold">Enviar como</label>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={email.operador_id}
                      onChange={(e) => mudarEmail('operador_id', e.target.value)}>
                      <option value="">— escolha —</option>
                      {(envio?.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.pronto ? '' : ' (sem senha de app)'}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={salvarEmails}
                    disabled={!!enviando || !email.para.trim()} title="Guardar Para e Cópia para os próximos envios desta empresa">
                    <i className={`fas ${enviando === 'salvar' ? 'fa-spinner fa-spin' : 'fa-save'} me-1`}></i>Salvar e-mails
                  </button>
                  {!email.previa && (
                    <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => verPrevia(email)} disabled={email.carregando}>
                      <i className={`fas ${email.carregando ? 'fa-spinner fa-spin' : 'fa-eye'} me-1`}></i>Atualizar prévia
                    </button>
                  )}
                </div>
                {email.carregando && !email.previa && <div className="small text-muted mt-3"><i className="fas fa-spinner fa-spin me-1"></i>Montando o e-mail…</div>}
                {email.previa && (<>
                  <div className="small mt-3 mb-2">
                    <div><b>Assunto:</b> {email.previa.assunto}</div>
                    <div><b>De:</b> {email.previa.de}</div>
                    <div><b>Para:</b> {email.previa.para || <span className="text-danger">informe o destinatário</span>}</div>
                    {email.previa.copia && <div><b>Cópia:</b> {email.previa.copia}</div>}
                    <div><b>Anexo:</b> <i className="fas fa-file-excel text-success me-1"></i>{email.previa.anexo}</div>
                  </div>
                  <div className="border rounded" style={{ maxHeight: 340, overflow: 'auto' }}>
                    <iframe title="Prévia do e-mail do controle de folgas" srcDoc={email.previa.corpo_previa || email.previa.corpo_html}
                      sandbox="" style={{ width: '100%', height: 340, border: 0 }} />
                  </div>
                </>)}
              </div>
              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">A planilha vai anexada — não precisa baixar nada.</span>
                <button className="btn btn-secondary" onClick={() => setEmail(null)} disabled={enviando === 'email'}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
                <button className="btn btn-success" onClick={enviarEmail}
                  disabled={enviando === 'email' || !email.previa || !email.para.trim() || !email.operador_id}
                  title={!email.previa ? 'Atualize a prévia antes de enviar' : undefined}>
                  <i className={`fas ${enviando === 'email' ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                  {enviando === 'email' ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div></div>
  );
}
